import { useEffect, useRef, useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"

import "./dashboard.css"

import DashboardNavbar from "../DashboardNavbar/DashbaordNavbar"
import DashboardMenu from "../MenuBar/dashboardMenu"
import { updateOrInsertBookLanguageStats } from "../../Helpers/statsApiService"
import PasswordConfirmation from "../PasswordConfirmation/PasswordConfirmation"
import BookData from "./BookData"
import RandomColor from "../../utils/randomColor"
import getAge from "../../utils/getAge"
import BookLanguageModal from "../../utils/bookLanguageModal"
import DashboardBook from "./DashboardBook"
import { clearSessionAndRedirect, getAuthToken, getStoredUserData, handleAuthError, isTokenExpired } from "../../Helpers/authSession"

import donut_dark from "../../assets/dashboard/donut.png"
import donut_dark_bottom from "../../assets/dashboard/Donut-light-big.png"

const config = require("../../Helpers/config.json")

const NewDashboard = () => {
  const navigate = useNavigate()
  const readingProgressRef = useRef(null)
  const initialized = useRef(false)
  const [selectedBook, setSelectedBook] = useState(null)
  const [showLangOption, setShowLangOption] = useState(false)
  const [description, setDescription] = useState("Reading books is a enriching activity where individuals engage with written content to gain knowledge, entertainment, and insights. It fosters literacy, critical thinking, and imagination, providing a means to explore diverse ideas, perspectives, and experiences. Whether for education or leisure, reading contributes to personal growth and enjoyment.")
  const [loading, setLoading] = useState(true)
  const [booksError, setBooksError] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [fadeClass, setFadeClass] = useState("fade-in")
  const [hoveredIndex0, setHoveredIndex0] = useState(null)
  const [readingBook, setReadingBook] = useState([])
  const [rawBooks, setRawBooks] = useState([]) // Store raw S3 data
  const [showModal, setShowModal] = useState(false)
  const [hoverLeave, setHoverLeave] = useState(false)

  const profileID = localStorage.getItem("profileId")
  const formatData = getStoredUserData()

  // PERFORMANCE: Use useMemo for data processing
  const { groupedBooks, topSectionData } = useMemo(() => {
    if (!rawBooks.length) return { groupedBooks: [], topSectionData: [] };

    console.time("Dashboard-DataMapping");

    // 1. Initial processing (Age selection and locking)
    const kidAge = getAge(localStorage.getItem("dob")); // Changed from kidDob to dob
    const freeStoryIds = config.freeStoryIds || [];
    const isSubscriptionActive = formatData?.plan?.type === "PAID";

    const processedBooks = rawBooks.map((d) => {
      let selectedPdf = null;
      if (d.pdfs?.length > 0) {
        let pdfForOlderKids = null;
        let pdfForYoungerKids = null;

        d.pdfs.forEach(pdf => {
          let match = pdf.pdfPath ? pdf.pdfPath.match(/(\d+)(?=\.pdf)/) : null;
          let pdfAge = match ? parseInt(match[1], 10) : null;
          if (pdfAge !== null) {
            if (pdfAge > 6) pdfForOlderKids = pdf;
            else pdfForYoungerKids = pdf;
          }
        });

        if (kidAge >= 8 && pdfForOlderKids) selectedPdf = pdfForOlderKids;
        else if (kidAge < 8 && pdfForYoungerKids) selectedPdf = pdfForYoungerKids;
        else selectedPdf = d.pdfs[0];
      }

      const isLocked = !isSubscriptionActive && (d.isLocked !== undefined ? d.isLocked : !freeStoryIds.includes(d.id));
      return { ...d, pdfs: selectedPdf ? [selectedPdf] : [], isLocked };
    });

    const cleanedData = processedBooks.filter((book) => book?.id !== null && book?.id !== undefined);
    const bookIdToStats = readingBook?.reduce((acc, item) => {
      acc[item.book_id] = item;
      return acc;
    }, {});

    // Genre colors cache
    const genreColorMap = {};
    const getGenreColor = (genre) => {
      if (genre.toLowerCase() === 'adventure') return '#F9A825';
      if (!genreColorMap[genre]) genreColorMap[genre] = RandomColor();
      return genreColorMap[genre];
    };

    const groupedBooksMap = {};
    const continueReadingBookIds = new Set();
    const freeGemsList = [];
    const completedBooks = [];
    const sortedData = [...processedBooks].sort((a, b) => new Date(b.created_At) - new Date(a.created_At));

    processedBooks.forEach((book) => {
      const stats = bookIdToStats?.[book.id];
      const readCount = Number(stats?.read_count || 0);
      let progress = Number(stats?.progress_percentage || 0);
      if (readCount > 0) progress = 100;

      const isInProgress = progress > 0 && progress < 100;
      const isCompleted = progress >= 100;

      const bookWithProgress = stats !== undefined ? { ...book, progress, isProgress: true } : book;

      // 1. Check for Free Gems (Unlocked stories)
      if (!isSubscriptionActive && !book.isLocked) {
        freeGemsList.push(bookWithProgress);
      }

      if (isInProgress) {
        if (!groupedBooksMap["Continue Reading"]) {
          groupedBooksMap["Continue Reading"] = { name: "Continue Reading", items: [], bg: "#f57f17", matchCount: 0 };
        }
        groupedBooksMap["Continue Reading"].items.push(bookWithProgress);
        continueReadingBookIds.add(book.id);
      } else if (isCompleted) {
        completedBooks.push(bookWithProgress);
      }
    });

    const newBooks = sortedData
      .filter((book) => !continueReadingBookIds.has(book.id))
      .slice(0, 5)
      .map((book) => ({ ...book, label: "New", labelColor: "red" }));
    const newBookIds = new Set(newBooks.map((book) => book.id));

    if (newBooks.length > 0) {
      groupedBooksMap["Recently Added"] = { name: "Newly Added", items: newBooks, bg: "red", matchCount: newBooks.length };
    }

    if (freeGemsList.length > 0) {
      const filteredFreeGems = freeGemsList.filter(book => !continueReadingBookIds.has(book.id));
      if (filteredFreeGems.length > 0) {
        groupedBooksMap["Free Gems"] = {
          name: "Free Gems",
          items: filteredFreeGems,
          bg: "linear-gradient(45deg, #00C853, #64DD17)", // Cool green gradient
          matchCount: filteredFreeGems.length
        };
      }
    }

    processedBooks.forEach((book) => {
      if (continueReadingBookIds.has(book.id) || newBookIds.has(book.id)) return;
      const genres = book.genre.split(",").map((genre) => genre.trim());
      genres.forEach((genre) => {
        if (!groupedBooksMap[genre]) {
          groupedBooksMap[genre] = { name: genre, items: [], bg: getGenreColor(genre), matchCount: 0 };
        }
        groupedBooksMap[genre].items.push(book);
        if (bookIdToStats?.[book.id] !== undefined) groupedBooksMap[genre].matchCount++;
      });
    });

    const finalGrouped = [];
    // Priority order for categories
    if (!isSubscriptionActive && groupedBooksMap["Free Gems"]) finalGrouped.push(groupedBooksMap["Free Gems"]);
    if (groupedBooksMap["Recently Added"]) finalGrouped.push(groupedBooksMap["Recently Added"]);
    if (groupedBooksMap["Continue Reading"]) finalGrouped.push(groupedBooksMap["Continue Reading"]);

    Object.keys(groupedBooksMap).forEach((key) => {
      const reservedKeys = ["Continue Reading", "Recently Added", "Free Gems"];
      if (!reservedKeys.includes(key)) finalGrouped.push(groupedBooksMap[key]);
    });

    if (completedBooks.length > 0) {
      finalGrouped.push({ name: "Completed Books", items: completedBooks, bg: "#4CAF50", matchCount: completedBooks.length });
    }

    console.timeEnd("Dashboard-DataMapping");
    return { groupedBooks: finalGrouped, topSectionData: cleanedData };
  }, [rawBooks, readingBook]);
  const setDesktopViewport = () => {
    const viewportMetaTag = document.querySelector('meta[name="viewport"]');
    viewportMetaTag.content = "width=1024"; // Set the width to your desired desktop width
  };

  useEffect(() => {
    setDesktopViewport();
    return () => {
      const viewportMetaTag = document.querySelector('meta[name="viewport"]');
      viewportMetaTag.content = "width=device-width, initial-scale=1.0";
    };
  }, []);

  const scrollRefs = useRef([])

  const getStoryBookLanguages = async (item) => {
    await setSelectedBook(item)
    await setShowLangOption(true)
  }

  const navigateToBook = (lang) => {
    navigate(`/book-detail?lang=${lang}`, { state: { SelectedBook: selectedBook, isFromNew: true } })
    updateOrInsertBookLanguageStats(localStorage.getItem("id"), localStorage.profileId, selectedBook.id, 1, lang)
  }

  const getBookReadingProgress = async () => {
    if (!formatData?.uid || !profileID) {
      console.warn("Skipping progress fetch: userId or profileId missing", { userId: formatData?.uid, profileId: profileID });
      return;
    }
    try {
      const authToken = getAuthToken();
      const res = await axios.get(`${config["baseUrlAws"]}/api/stats/books/getBookReadingProgress/?userId=${formatData?.uid}&profileId=${profileID}`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      })
      if (res && res?.data && res?.data?.data) {
        setReadingBook(res?.data?.data)
      }
    } catch (error) {
      if (handleAuthError(error)) return;

      console.error("Error while getting book reading progress:", {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config?.url
      })
    }
  }

  const fetchBooks = async (progressData) => {
    try {
      console.time("Dashboard-FetchBooks-Total");
      setLoading(true);
      setBooksError(false);
      const authToken = getAuthToken();
      console.time("Dashboard-API-S3Book");
      const response = await axios.get(`${config["baseUrlAws"]}/s3/book/${process.env.REACT_APP_ORGANIZATION_NAME}`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      })
      console.timeEnd("Dashboard-API-S3Book");
      setRawBooks(response?.data?.data || []);
      console.timeEnd("Dashboard-FetchBooks-Total");
    } catch (error) {
      if (handleAuthError(error)) return;

      setBooksError(true);
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const initDashboard = async () => {
      const authToken = getAuthToken();

      if (!authToken || isTokenExpired(authToken)) {
        clearSessionAndRedirect();
        return;
      }

      // Fetch progress first
      let progress = [];
      if (formatData?.uid && profileID) {
        try {
          const res = await axios.get(`${config["baseUrlAws"]}/api/stats/books/getBookReadingProgress/?userId=${formatData?.uid}&profileId=${profileID}`, {
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          })
          if (res && res?.data && res?.data?.data) {
            progress = res?.data?.data;
            setReadingBook(progress);
          }
        } catch (error) {
          if (handleAuthError(error)) return;

          console.error("Error while getting book reading progress in init:", error);
        }
      }
      // Then fetch books and pass the progress data directly
      await fetchBooks(progress);
    }

    initDashboard();
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeClass("fade-out")
      setTimeout(() => {
        setCurrentIndex((prevIndex) => {
          const newIndex = prevIndex < topSectionData.length - 1 ? prevIndex + 1 : 0
          return newIndex
        })
        setTimeout(() => { setFadeClass("fade-in") }, 400)
      }, 200)
    }, 13000)

    return () => clearInterval(interval)
  }, [topSectionData.length])

  if (loading || booksError) {
    return (
      <div className="dashboard-loading-screen">
        <div className="dashboard-loading-content" role={booksError ? 'alert' : 'status'}>
          {!booksError && <span className="dashboard-loading-spinner" aria-hidden="true" />}
          <h1>{booksError ? 'Your stories could not load' : 'A little magic is on its way'}</h1>
          <p>{booksError ? 'Please try loading your library again.' : 'Getting your story library ready...'}</p>
          {booksError && <button onClick={() => fetchBooks()}>Try again</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="new-dashboard-wrapper">
      <div className="dashbaord-donut-container">
        <img src={donut_dark} className="dashbaord-donut" alt="" />
      </div>
      <DashboardNavbar setShowModal={setShowModal} />
      <img src={donut_dark_bottom} className="dashbaord-donut-bottom" alt="" />
      <div className="content-wrrapers">
        <DashboardMenu setShowModal={setShowModal} />
        <div className="sub-content-wrrapers">
          <div className="cards-text-container">
            <DashboardBook setSelectedBook={setSelectedBook} description={description} topSectionData={topSectionData} currentIndex={currentIndex} fadeClass={fadeClass} />
            <BookData allBooks={groupedBooks} scrollRefs={scrollRefs} readingProgressRef={readingProgressRef} getStoryBookLanguages={getStoryBookLanguages} setHoveredIndex0={setHoveredIndex0} setHoverLeave={setHoverLeave} hoveredIndex0={hoveredIndex0} hoverLeave={hoverLeave} setSelectedBook={setSelectedBook} />
            {showLangOption && (<BookLanguageModal selectedBook={selectedBook} updateShowLangOption={() => setShowLangOption(false)} navigateToBook={navigateToBook} />)}
          </div>
        </div>
      </div>
      {<PasswordConfirmation showModal={showModal} setShowModal={setShowModal} route={"/account"} />}
      <div>
      </div>
    </div>
  )
}

export default NewDashboard
